import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Ear, Brain, Zap, Check, Users, Headphones, BarChart3, Gamepad2, Monitor, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Solution = () => {
  return (
    <div data-testid="solution-page" className="bg-background">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center hero-gradient noise-overlay overflow-hidden" data-testid="solution-hero">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container-asp relative z-10 py-32">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="max-w-4xl mx-auto text-center"
          >
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">
              Notre technologie
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mt-4 mb-6">
              Un système<br />
              <span className="text-gradient">révolutionnaire</span>
            </h1>
            <p className="text-xl text-white/60 leading-relaxed max-w-2xl mx-auto">
              Une approche modulaire qui s'adapte à votre niveau et vos besoins. 
              De l'éducation auditive de base à la personnalisation tactique avancée.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brique 1 - Éducation Auditive */}
      <section className="section-padding bg-background" data-testid="brique-1">
        <div className="container-asp">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Ear className="w-4 h-4" />
                Fondamentaux
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                Éducation Auditive
              </h2>
              <p className="text-xl text-white/50 mb-6">La base pour tous les clubs</p>
              
              <p className="text-white/60 leading-relaxed mb-6">
                La première étape indispensable : éduquer l'oreille de vos joueurs à l'audio 3D binaural. 
                Un système très perturbant au départ, qui devient une seconde nature avec l'entraînement.
              </p>

              <div className="space-y-4 mb-8">
                <h3 className="text-lg font-semibold text-white">Ce que vos joueurs apprennent :</h3>
                <ul className="space-y-3">
                  {[
                    'Spatialisation sonore : gauche/droite, avant/arrière, distance',
                    'Reconnaissance instantanée : identifier les signaux en millisecondes',
                    'Automatismes auditifs : réflexes décisionnels sans contact visuel',
                    'Positionnement spatial : distance, timbre, direction dans l\'espace'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/50">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <Card className="card-glass p-6">
                <h4 className="text-white font-medium mb-3">Exemple de séance :</h4>
                <p className="text-white/50 text-sm italic">
                  Phase défensive : le joueur, casque immersif, entend une annonce tactique spatialisée. 
                  Il doit identifier instantanément la direction, la distance, et déclencher le bon mouvement 
                  sur sa manette. Temps de réaction mesuré en millisecondes. Objectif : passer sous les 400ms.
                </p>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <Card className="card-glass p-8">
                <h3 className="text-xl font-semibold text-white mb-6">Phases de jeu couvertes</h3>
                <div className="space-y-4">
                  {[
                    { name: 'Attaque structurée', count: '8 séquences' },
                    { name: 'Attaque de transition', count: '5 séquences' },
                    { name: 'Défense alignée', count: '6 séquences' },
                    { name: 'Défense en repli', count: '4 séquences' },
                    { name: 'Mêlée fermée', count: '3 phases' },
                    { name: 'Mêlée ouverte', count: '2 phases' },
                    { name: 'Touche offensive', count: '5 combinaisons' },
                    { name: 'Touche défensive', count: '4 configurations' },
                    { name: 'Ruck - Positionnement', count: '6 situations' },
                    { name: 'Contre-attaque', count: '4 scénarios' },
                    { name: 'Gestion du jeu au pied', count: '3 phases' },
                    { name: 'Situations de surnombre', count: '5 configurations' }
                  ].map((phase, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <span className="text-white font-medium text-sm">{phase.name}</span>
                      <span className="text-white/40 text-xs">{phase.count}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-primary" />
                    <span className="text-white/50 text-sm">Conçu pour les clubs professionnels et semi-pros</span>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brique 2 - Programme Club */}
      <section className="section-padding bg-gradient-to-b from-background to-black/50" data-testid="brique-2">
        <div className="container-asp">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <Card className="card-glass p-8">
                <h3 className="text-xl font-semibold text-white mb-6">Processus d'intégration</h3>
                <div className="space-y-6">
                  {[
                    { step: '01', title: 'Analyse', desc: 'Étude de votre playbook et de vos besoins spécifiques' },
                    { step: '02', title: 'Captation', desc: 'Sessions terrain pour comprendre vos phases de jeu' },
                    { step: '03', title: 'Modélisation', desc: 'Création de votre environnement audio personnalisé' },
                    { step: '04', title: 'Intégration', desc: 'Déploiement dans l\'application dédiée à votre club' },
                    { step: '05', title: 'Accompagnement', desc: 'Formation et suivi tout au long de la saison' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-secondary font-bold text-sm">{item.step}</span>
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{item.title}</h4>
                        <p className="text-white/40 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Brain className="w-4 h-4" />
                Sur mesure
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                Programme Club
              </h2>
              <p className="text-xl text-white/50 mb-6">100% personnalisé à votre équipe</p>
              
              <p className="text-white/60 leading-relaxed mb-6">
                Intégration complète de vos stratégies spécifiques. Vos joueurs s'entraînent 
                sur vos vraies phases de jeu, dans des conditions réalistes.
              </p>

              <Card className="card-glass p-6 mb-6 border-secondary/20">
                <div className="flex items-start gap-4">
                  <Brain className="w-6 h-6 text-secondary mt-1" />
                  <div>
                    <h4 className="text-white font-medium mb-2">IA Propriétaire</h4>
                    <p className="text-white/50 text-sm">
                      Notre technologie d'intelligence artificielle permet une personnalisation 
                      poussée et une adaptation en temps réel aux besoins de chaque joueur.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="card-glass p-6">
                <h4 className="text-white font-medium mb-3">Exemple concret :</h4>
                <p className="text-white/50 text-sm italic">
                  Votre phase de jeu "Tesla" : le n°10 a 3 options possibles après réception. 
                  Vos joueurs peuvent la pratiquer en audio immersif, encore et encore, 
                  jusqu'à ce que les automatismes soient parfaits.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brique 3 - Flexibilité */}
      <section className="section-padding bg-black/50" data-testid="brique-3">
        <div className="container-asp">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Avantage compétitif
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                Flexibilité Tactique
              </h2>
              <p className="text-xl text-white/50 mb-6">Préparez l'imprévisible</p>
              
              <p className="text-white/60 leading-relaxed mb-6">
                En phase de récupération de balle, les joueurs ne sont pas toujours en position standard. 
                Cette dimension permet de s'entraîner à toutes les situations chaotiques et de créer 
                des surprises tactiques.
              </p>

              <div className="space-y-4 mb-8">
                <h3 className="text-lg font-semibold text-white">Fonctionnalités avancées :</h3>
                <ul className="space-y-3">
                  {[
                    'Permutation dynamique des postes selon les scénarios',
                    'Préparation aux situations de chaos (ruck gratté, transition rapide)',
                    'Scénarios surprises pour déstabiliser l\'adversaire',
                    'Adaptabilité totale à toute configuration de jeu'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/50">
                      <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <Card className="card-glass p-6 border-accent/20">
                <h4 className="text-white font-medium mb-3">Cas d'usage :</h4>
                <p className="text-white/50 text-sm">
                  Match contre une équipe qui a analysé vos phases standards ?<br/>
                  <span className="text-accent">Préparez un scénario inversé. Surprise tactique garantie.</span>
                </p>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <Card className="card-glass p-8">
                <h3 className="text-xl font-semibold text-white mb-6">Avantage compétitif</h3>
                <p className="text-white/50 mb-8">
                  Permet de créer des scénarios tactiques inattendus pour déstabiliser l'adversaire 
                  qui a analysé vos phases standards.
                </p>
                
                <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl p-6 border border-accent/20">
                  <h4 className="text-white font-medium mb-4">Exemple : Match décisif</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex gap-3">
                      <span className="text-red-400 font-medium shrink-0">Problème:</span>
                      <span className="text-white/50">L'adversaire a analysé vos phases standards</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-accent font-medium shrink-0">Solution:</span>
                      <span className="text-white/50">Scénario inversé préparé à l'avance</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-medium shrink-0">Résultat:</span>
                      <span className="text-white/60">Surprise tactique, adversaire désorganisé</span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parcours utilisateur */}
      <section className="section-padding bg-background" data-testid="parcours-section">
        <div className="container-asp">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-medium tracking-wider uppercase">Expérience utilisateur</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mt-4">Deux interfaces</h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Coach */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
            >
              <Card className="card-glass h-full p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Monitor className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-white">Côté Coach</h3>
                    <p className="text-white/50">Back-office web</p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-8">
                  {[
                    { title: 'Configuration scénarios', desc: 'Sélection des phases, préparation hebdomadaire' },
                    { title: 'Attribution des rôles', desc: 'Définition des configurations par joueur' },
                    { title: 'Personnalisation', desc: 'Adaptation aux besoins spécifiques' },
                    { title: 'Suivi de progression', desc: 'Dashboard KPI, statistiques par joueur' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-3 rounded-lg bg-white/5">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-white font-medium text-sm">{item.title}</h4>
                        <p className="text-white/40 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Card className="bg-white/5 p-4 border-0">
                  <p className="text-white/50 text-sm italic">
                    "Cette semaine, on joue contre Paris → Je prépare 3 phases spécifiques → 
                    Les joueurs les retrouvent dans leur app"
                  </p>
                </Card>
              </Card>
            </motion.div>

            {/* Joueur */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="card-glass h-full p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center">
                    <Smartphone className="w-7 h-7 text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-white">Côté Joueur</h3>
                    <p className="text-white/50">Application mobile</p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-8">
                  {[
                    { title: 'Connexion par position', desc: 'Accès aux phases assignées par le coach' },
                    { title: 'Entraînement immersif', desc: 'Casque audio 3D, concentration totale' },
                    { title: 'Préparation mentale', desc: 'Écoute des séquences avant le match' },
                    { title: 'Entraînement autonome', desc: 'Possibilité de pratiquer seul' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-3 rounded-lg bg-white/5">
                      <Check className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="text-white font-medium text-sm">{item.title}</h4>
                        <p className="text-white/40 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Card className="bg-white/5 p-4 border-0">
                  <p className="text-white/50 text-sm italic">
                    Un joueur peut s'entraîner seul, à tout moment, 
                    sur les phases assignées par son coach.
                  </p>
                </Card>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* KPI */}
      <section className="section-padding bg-gradient-to-b from-background to-black/50" data-testid="kpi-section">
        <div className="container-asp">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="text-center mb-16"
          >
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">Mesure de performance</span>
            <h2 className="text-4xl font-bold text-white mt-4">KPI et progression</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BarChart3, title: 'Taux de réponse', desc: 'Pourcentage de bonnes décisions par séance', metric: '% par séance' },
              { icon: Zap, title: 'Temps de réaction', desc: 'Délai entre signal et action', metric: 'Millisecondes' },
              { icon: Gamepad2, title: 'Précision directionnelle', desc: 'Exactitude spatiale (X, Y, Z)', metric: 'Degré d\'écart' },
              { icon: Check, title: 'Timing décisionnel', desc: 'Action au bon moment', metric: 'Décalage temporel' },
              { icon: Users, title: 'Progression équipe', desc: 'Évolution sur plusieurs séances', metric: 'Courbe de progression' },
              { icon: Headphones, title: 'Validation d\'étapes', desc: 'Paliers d\'apprentissage franchis', metric: 'Checkpoints' }
            ].map((kpi, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: i * 0.05 }}
              >
                <Card className="card-glass p-6 h-full">
                  <kpi.icon className="w-8 h-8 text-primary mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{kpi.title}</h3>
                  <p className="text-white/50 text-sm mb-3">{kpi.desc}</p>
                  <span className="text-primary/70 text-xs font-medium">{kpi.metric}</span>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-background" data-testid="solution-cta">
        <div className="container-asp text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Prêt à transformer<br />
              <span className="text-gradient">la performance de votre équipe ?</span>
            </h2>
            <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
              Contactez-nous pour discuter de votre projet. 
              Nous construisons ensemble la solution adaptée à vos besoins.
            </p>
            <Link to="/contact">
              <Button className="btn-secondary text-base px-10 py-4 group" data-testid="solution-cta-btn">
                S'inscrire sur la liste d'attente
                <ArrowUpRight className="ml-2 w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Solution;
